"use client"

import { useState } from "react"
import { Loader2 } from "lucide-react"
import Image from "next/image"
import { updateCartItemCustomization } from "@lib/data/cart"
import { convertToLocale } from "@lib/util/money"
import {
  CUSTOMIZATION_LIMITS,
  CUSTOMIZATION_PATCHES,
} from "@constants/product-page"
import { SHIRT_PRINTING_PLAYERS_AZ } from "@constants/shirt-printing"
import { HttpTypes } from "@medusajs/types"
import FilterTabs from "@modules/common/components/filter-tabs"

/** Shirts print the surname only (matches real printing convention, e.g.
 * "Cristiano Ronaldo" -> "Ronaldo"). Players whose surname still exceeds the
 * backend's custom_name length cap (e.g. "Alexander-Arnold") are excluded
 * below rather than silently truncated — a shirt printed with a cut-off name
 * is worse than not offering that player. */
const surnameFor = (fullName: string) => {
  const parts = fullName.trim().split(" ")
  return parts.length > 1 ? parts.slice(1).join(" ") : fullName
}

const PLAYER_NAMES = Array.from(
  new Set(Object.values(SHIRT_PRINTING_PLAYERS_AZ).flat().map((p) => p.name))
)
  .filter((name) => surnameFor(name).length <= CUSTOMIZATION_LIMITS.nameMaxLength)
  .sort((a, b) => a.localeCompare(b))

type CustomizationTab = "player" | "custom"

const CUSTOMIZATION_TABS = [
  { id: "player", label: "Choose Player Name" },
  { id: "custom", label: "Custom Name" },
]

type LineItemCustomizationEditorProps = {
  item: HttpTypes.StoreCartLineItem
  currencyCode: string
  onClose: () => void
  /** Always stack fields vertically, for narrow contexts (e.g. the mini
   * cart drawer) where `md:` viewport breakpoints don't reflect the
   * available width — the drawer stays narrow even on desktop viewports. */
  compact?: boolean
}

/**
 * Inline "edit customisation" panel, expanded in place within a cart line
 * item — matches the reference site's cart drawer (Name/Number fields +
 * Update button, no navigation) instead of linking out to the product page.
 */
export default function LineItemCustomizationEditor({
  item,
  currencyCode,
  onClose,
  compact = false,
}: LineItemCustomizationEditorProps) {
  const metadata = (item.metadata as Record<string, unknown> | null) ?? null

  const [customName, setCustomName] = useState(
    typeof metadata?.custom_name === "string" ? metadata.custom_name : ""
  )
  const [customNumber, setCustomNumber] = useState(
    typeof metadata?.custom_number === "string" ? metadata.custom_number : ""
  )
  const [selectedPatch, setSelectedPatch] = useState(
    typeof metadata?.patch_id === "string" && metadata.patch_id ? metadata.patch_id : "none"
  )
  // The stored custom_name is a surname (see surnameFor), so it can't be
  // reverse-matched to a specific dropdown entry — default to the free-text
  // tab whenever a customisation already exists, so re-opening the editor
  // shows exactly what's saved instead of guessing a player.
  const [activeTab, setActiveTab] = useState<CustomizationTab>(
    customName ? "custom" : "player"
  )
  // Full player name for the "player" tab's dropdown value — the surname
  // actually submitted (see surnameFor) is derived from this, not stored
  // separately, so the two can't drift out of sync.
  const [selectedPlayer, setSelectedPlayer] = useState("")
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasExistingCustomization =
    !!metadata?.custom_name ||
    !!metadata?.custom_number ||
    (typeof metadata?.patch_id === "string" && metadata.patch_id !== "none" && metadata.patch_id !== "")

  // Falls back to the existing customName if the player tab is active but
  // nothing's been picked yet — browsing to this tab without making a
  // selection must not wipe a name that was already saved on the line item.
  const nameToSubmit =
    activeTab === "player" && selectedPlayer ? surnameFor(selectedPlayer) : customName

  const formatPrice = (amount: number) =>
    convertToLocale({ amount, currency_code: currencyCode })

  const handleUpdate = async () => {
    setSaving(true)
    setError(null)

    const result = await updateCartItemCustomization({
      lineItemId: item.id,
      customName: nameToSubmit,
      customNumber,
      patchId: selectedPatch,
    })

    setSaving(false)

    if (!result.success) {
      setError(result.error || "Failed to update customisation")
      return
    }

    onClose()
  }

  const handleRemove = async () => {
    setRemoving(true)
    setError(null)

    const result = await updateCartItemCustomization({
      lineItemId: item.id,
      customName: "",
      customNumber: "",
      patchId: "none",
    })

    setRemoving(false)

    if (!result.success) {
      setError(result.error || "Failed to remove customisation")
      return
    }

    setCustomName("")
    setSelectedPlayer("")
    setCustomNumber("")
    setSelectedPatch("none")
    onClose()
  }

  const inputClassName = compact
    ? "flex-1 min-w-0 px-3 py-2 text-xs font-medium text-gray-900 bg-white border border-brand-navy rounded-lg outline-none focus:border-brand-primary transition-colors"
    : "w-full md:w-28 px-3 py-2 text-xs font-medium text-gray-900 bg-white border border-brand-navy rounded-lg outline-none focus:border-brand-primary transition-colors"

  const tabToggle = (
    <FilterTabs
      filters={CUSTOMIZATION_TABS}
      activeFilter={activeTab}
      onFilterChange={(id) => setActiveTab(id as CustomizationTab)}
      ariaLabel="Personalisation type"
      inline
    />
  )

  const nameNumberInputs = (
    <>
      {activeTab === "player" ? (
        <select
          value={selectedPlayer}
          onChange={(e) => setSelectedPlayer(e.target.value)}
          aria-label="Choose player name"
          className={inputClassName}
        >
          <option value="">Choose a player</option>
          {PLAYER_NAMES.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          value={customName}
          onChange={(e) =>
            setCustomName(e.target.value.slice(0, CUSTOMIZATION_LIMITS.nameMaxLength))
          }
          placeholder="Name"
          maxLength={CUSTOMIZATION_LIMITS.nameMaxLength}
          aria-label="Custom name"
          className={inputClassName}
        />
      )}
      <input
        type="text"
        value={customNumber}
        onChange={(e) =>
          setCustomNumber(
            e.target.value.slice(0, CUSTOMIZATION_LIMITS.numberMaxLength).replace(/[^0-9]/g, "")
          )
        }
        placeholder="No"
        maxLength={CUSTOMIZATION_LIMITS.numberMaxLength}
        aria-label="Custom number"
        className={inputClassName}
      />
    </>
  )

  return (
    <div
      className={
        compact
          ? "mt-2 p-3 bg-[#F7F7F7] rounded-lg flex flex-col gap-2.5"
          : "mt-3 p-3 md:px-4 md:py-3.5 bg-[#F7F7F7] rounded-lg flex flex-col md:flex-row md:items-center gap-3"
      }
    >
      {tabToggle}

      {compact ? (
        <div className="flex gap-2">{nameNumberInputs}</div>
      ) : (
        nameNumberInputs
      )}

      <div className="flex flex-wrap gap-2">
        {CUSTOMIZATION_PATCHES.filter((patch) => patch.id !== "none").map((patch) => {
          const isSelected = selectedPatch === patch.id
          return (
            <button
              key={patch.id}
              type="button"
              onClick={() => setSelectedPatch(isSelected ? "none" : patch.id)}
              aria-pressed={isSelected}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-[10px] border text-xs font-medium text-gray-700 transition-colors ${
                isSelected
                  ? "border-brand-navy bg-white"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              {patch.image && (
                <Image
                  src={patch.image}
                  alt=""
                  width={32}
                  height={32}
                  className={compact ? "w-6 h-6 object-contain" : "w-8 h-8 object-contain"}
                />
              )}
              {patch.name} (+{formatPrice(patch.price)})
            </button>
          )
        })}
      </div>

      {error && <p className="text-[11px] text-brand-accent">{error}</p>}

      <div
        className={
          compact
            ? "flex items-center gap-2"
            : "flex items-center gap-4 md:ml-auto md:gap-3"
        }
      >
        {hasExistingCustomization && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={saving || removing}
            className={
              compact
                ? "flex-1 text-center text-xs font-medium text-gray-500 hover:text-brand-accent disabled:opacity-60 flex items-center justify-center gap-1 py-2"
                : "flex-1 md:flex-none text-center text-xs font-medium text-red-600 md:text-gray-400 hover:text-brand-accent disabled:opacity-60 flex items-center justify-center gap-1"
            }
          >
            {removing && <Loader2 className="w-3 h-3 animate-spin" />}
            Remove
          </button>
        )}
        <button
          type="button"
          onClick={handleUpdate}
          disabled={saving || removing}
          className={
            compact
              ? "flex-1 py-2 px-3 bg-brand-primary text-white text-xs font-semibold rounded-md hover:bg-brand-primary-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
              : "flex-1 md:flex-none py-2 px-5 bg-brand-primary text-white text-xs font-semibold rounded-md hover:bg-brand-primary-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
          }
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Update Customization
        </button>
      </div>
    </div>
  )
}
